<template>
  <b-container
    class="accordion-list"
    fluid
  >
    <accordion-panel
      v-for="(norm, index) in norms"
      header-class="accordion-list__norm-accordion"
      :key="index"
      :id="norm.norm"
      :collapsed.sync="norm.collapsed"
    >
      <template #title>
        <b-row>
          <b-col cols="9">
            <b-checkbox
              class="d-inline-block"
              :checked="isAllDiagnosticsFromNormSelected(norm)"
              @change="markAllDiagnosticsFromNorm($event, norm)"
            >
              <span class="accordion-list__norm-title">
                {{ $t(`diagnostics-configurations.modules.norms.${norm.norm}`) }}
              </span>
            </b-checkbox>
          </b-col>
          <b-col
            v-if="!norm.methods"
            class="p-0"
            cols="2"
          >
            <strong> {{ $t('diagnostics-configurations.classification') }} </strong>
          </b-col>
          <b-col
            v-if="!norm.methods"
            class="p-0"
            cols="1"
          >
            <strong> {{ $t('diagnostics-configurations.alarm') }} </strong>
          </b-col>
        </b-row>
      </template>
      <div v-if="norm.methods">
        <accordion-panel
          v-for="(method, index) in norm.methods"
          header-class="accordion-list__method-accordion"
          :key="index"
          :id="method.method"
          :collapsed.sync="method.collapsed"
        >
          <template #title>
            <b-row>
              <b-col cols="9">
                <b-checkbox
                  class="d-inline-block"
                  :checked="isAllDiagnosticsFromMethodSelected(method)"
                  @change="markAllDiagnosticsFromMethod($event, method)"
                >
                  <span class="accordion-list__method-title">
                    {{ $t(`diagnostics-configurations.modules.methods.${method.method}`) }}
                  </span>
                </b-checkbox>
              </b-col>
              <b-col
                class="p-0"
                cols="2"
              >
                <strong> {{ $t('diagnostics-configurations.classification') }} </strong>
              </b-col>
              <b-col
                class="p-0"
                cols="1"
              >
                <strong> {{ $t('diagnostics-configurations.alarm') }} </strong>
              </b-col>
            </b-row>
          </template>
          <b-row
            class="accordion-list__diagnosis-row"
            v-for="diagnosis in method.diagnostics"
            :key="diagnosis.id"
          >
            <b-col cols="9">
              <b-checkbox
                class="accordion-list__diagnosis-title d-inline-block"
                :checked="isDiagnosisSelected(diagnosis.id)"
                @change="markDiagnosis($event, diagnosis)"
              >
                <span class="font-weight-normal">
                  {{ diagnosis.name }}
                </span>
              </b-checkbox>
            </b-col>
            <b-col
              class="p-0"
              cols="2"
            >
              <classification-type-column
                class="accordion-list__classification-cell"
                :classifications="diagnosis.classifications"
                @click-link="setDiagnosticDetailsModalInfo(diagnosis)"
              />
            </b-col>
            <b-col
              class="p-0 pl-3"
              cols="1"
            >
              <alarm-status-column
                :diagnostic="diagnosis"
                :alarms="diagnosis.alarms"
                @click-link="setDiagnosticDetailsModalInfo(diagnosis)"
              />
            </b-col>
          </b-row>
        </accordion-panel>
      </div>
      <div v-else>
        <b-row
          class="accordion-list__diagnosis-row"
          v-for="diagnosis in norm.diagnostics"
          :key="diagnosis.id"
        >
          <b-col cols="9">
            <b-checkbox
              class="accordion-list__diagnosis-title d-inline-block"
              :checked="isDiagnosisSelected(diagnosis.id)"
              @change="markDiagnosis($event, diagnosis)"
            >
              <span class="font-weight-normal">
                {{ diagnosis.name }}
              </span>
            </b-checkbox>
          </b-col>
          <b-col
            class="p-0"
            cols="2"
          >
            <classification-type-column
              class="accordion-list__classification-cell"
              :classifications="diagnosis.classifications"
              @click-link="setDiagnosticDetailsModalInfo(diagnosis)"
            />
          </b-col>
          <b-col
            class="p-0 pl-3"
            cols="1"
          >
            <alarm-status-column
              :diagnostic="diagnosis"
              :alarms="diagnosis.alarms"
              @click-link="setDiagnosticDetailsModalInfo(diagnosis)"
            />
          </b-col>
        </b-row>
      </div>
    </accordion-panel>
    <diagnostic-details-modal
      v-model="showDiagnosticDetailsModal"
      :title="diagnosticDetailsModalInfo.name"
      :filter="filter"
      :payload="diagnosticDetailsModalInfo"
    />
  </b-container>
</template>

<script>
import AccordionPanel from '@/components/AccordionPanel';
import AlarmStatusColumn from '@/components/AlarmStatusColumn';
import DiagnosticDetailsModal from '@/domain/components/generalPanel/adminPanel/diagnosticsConfigurations/DiagnosticDetailsModal';
import ClassificationTypeColumn from '@/components/ClassificationTypeColumn';

export default {
  name: 'AccordionList',
  components: {
    AlarmStatusColumn,
    AccordionPanel,
    DiagnosticDetailsModal,
    ClassificationTypeColumn,
  },
  props: {
    norms: {
      type: Array,
      default: () => ([]),
    },
    selectedDiagnostics: {
      type: Array,
      default: () => ([]),
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    diagnosticDetailsModalInfo: {},
    showClassificationModal: false,
    showDiagnosticDetailsModal: false,
    showDetailsModal: false,
  }),
  computed: {
    selectedDiagnosticsProxy: {
      get() {
        return this.selectedDiagnostics;
      },
      set(value) {
        this.$emit('update:selected-diagnostics', value);
      },
    },
  },
  methods: {
    openDiagnosticDetailsModal() {
      this.showDiagnosticDetailsModal = true;
    },
    setDiagnosticDetailsModalInfo(diagnosis) {
      this.diagnosticDetailsModalInfo = diagnosis;
      this.openDiagnosticDetailsModal();
    },
    isAllDiagnosticsFromMethodSelected(method) {
      return method.diagnostics.every(item => this.isDiagnosisSelected(item.id));
    },
    isAllDiagnosticsFromNormSelected(norm) {
      const isAllDiagnosticsFromNormSelected = (norm.diagnostics || []).length
        && norm.diagnostics.every(
          item => this.isDiagnosisSelected(item.id),
        );
      const isAllDiagnosticsFromNormMethodsSelected = (norm.methods || []).length
        && norm.methods.every(
          method => method.diagnostics.every(
            diagnosis => this.isDiagnosisSelected(diagnosis.id),
          ),
        );
      return isAllDiagnosticsFromNormSelected || isAllDiagnosticsFromNormMethodsSelected;
    },
    isDiagnosisSelected(id) {
      return !!this.selectedDiagnosticsProxy.find(item => item.id === id);
    },
    markAllDiagnosticsFromNorm(state, norm) {
      this.$emit('mark-all-from-norm', state, norm);
    },
    markAllDiagnosticsFromMethod(state, method) {
      this.$emit('mark-all-from-method', state, method);
    },
    markDiagnosis(state, diagnosis) {
      this.$emit('mark-diagnosis', state, diagnosis);
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  .accordion-list
    overflow-x: auto
    &__norm-title
      font-size: 1.1rem
    &__diagnosis-row
      margin: 0
      padding: 8px 8% 8px 0
      background: var(--color-table-body-bg)
      &:nth-child(2n)
        background: var(--color-background-regular)
    &__diagnosis-title
      padding-left: 40px
    &__classification-cell
      display: flex
    ::v-deep .accordion-list
      &__norm-accordion
        padding: 15px 10px !important
        border-radius: 0
        background-color: var(--color-gray-medium-light) !important
      &__method-accordion
        padding: 10px !important
        border-radius: 0
        background-color: var(--color-gray-medium-highlight) !important
        .custom-control
          margin-left: 10px
    ::v-deep .accordion-panel
        min-width: 768px
</style>

<style lang="sass">
  .accordion-list
    .accordion-panel
      &__title-container
        width: calc(100% - 60px)
      &__slot-container
        margin: 0 !important
</style>
